import React, { useMemo } from 'react';

import { FormControlLabel, Grid, makeStyles, Radio, RadioGroup, Typography } from '@material-ui/core';

import { WorkspaceProductType } from '../../models/Workspace';
import PaymentUtils from '../../utils/payment.utils';
import SimpleTooltip from '../simpleTooltip/SimpleTooltip';

const useStyles = makeStyles((theme) => {
  return {
    packageSelectionHeader: {
      fontSize: '15px',
      fontWeight: 'bold',
      marginBottom: '0px',
      textAlign: 'left',
    },
    packageSelectionSub: {
      fontSize: '13px',
      marginBottom: '0px',
      marginTop: '0px',
      textAlign: 'left',
    },
    radioContainer: {
      alignItems: 'center',
      display: 'flex',
      flexWrap: 'wrap',
    },
    tooltipContainer: {
      marginLeft: '-8px',
    },
  };
});

export default ({ onUpdatePaymentMethod, selectedPaymentMethod, selectedPackage, userDetails, onUpdateSelectedPackage, discountData }) => {
  const classes = useStyles();
  const isWhiteGlove = useMemo(() => [userDetails?.workspace?.productType].includes(WorkspaceProductType.whiteGlove.value), [userDetails]);

  const handleUpdatePaymentMethod = (_event, value) => {
    if (onUpdatePaymentMethod) onUpdatePaymentMethod(value);
  };

  const handleUpdateSelectedPackage = (_event, value) => {
    if (onUpdateSelectedPackage) onUpdateSelectedPackage(value);
  };

  const pricing = useMemo(() => {
    const price = {
      express: parseFloat(PaymentUtils.PACKAGE_EXPRESS_VIDEO_REPORT.plan.price),
      standard: parseFloat(PaymentUtils.PACKAGE_STANDARD_VIDEO_REPORT.plan.price),
    };

    // eslint-disable-next-line camelcase
    if (discountData?.percent_off) {
      price.express *= (100 - discountData.percent_off) / 100;
      price.standard *= (100 - discountData.percent_off) / 100;
    } else if (isWhiteGlove) {
      price.express = (price.express * 90) / 100;
      price.standard = (price.standard * 90) / 100;
    }

    if (`${price.express}`.includes('.')) price.express = price.express.toFixed(2);
    if (`${price.standard}`.includes('.')) price.standard = price.standard.toFixed(2);

    if (!price.express) price.express = 'FREE';
    else price.express = `$${price.express}`;

    if (!price.standard) price.standard = 'FREE';
    else price.standard = `$${price.standard}`;

    return price;
  }, [isWhiteGlove, discountData]);

  return (
    <Grid container>
      <Grid item xs={12} md={6} style={{ height: '100%', padding: '0 3rem' }}>
        <RadioGroup onChange={handleUpdatePaymentMethod} value={selectedPaymentMethod}>
          <div className={classes.radioContainer}>
            <FormControlLabel
              control={<Radio color="primary" checked={selectedPaymentMethod === 'patient-pay'} />}
              label="Patient to make payment themselves."
              value="patient-pay"
            />
            <div className={classes.tooltipContainer}>
              <SimpleTooltip
                title={`
                  If you choose for the patient to pay for themselves, they will be sent a link to 
                  complete their payment online before we begin working on their video report.
                  `}
              />
            </div>
          </div>

          <div className={classes.radioContainer}>
            <FormControlLabel control={<Radio color="primary" checked={selectedPaymentMethod === 'admin-pay'} />} label="We will pay for patient now." value="admin-pay" />
            <div className={classes.tooltipContainer}>
              <SimpleTooltip
                title={`
                  If you choose to pay for the patient now, you will be directed to a payment screen 
                  where you can make payment using credit card right away. This option typically 
                  results in an overall faster turnaround time.
                `}
              />
            </div>
          </div>
        </RadioGroup>
      </Grid>

      {selectedPaymentMethod === 'admin-pay' && (
        <Grid item xs={12} md={6} style={{ height: '100%', padding: '0 3rem 0' }}>
          <Typography className={classes.packageSelectionHeader}>Package Selection</Typography>
          <Typography className={classes.packageSelectionSub}>Please select the required package and turnaround time for your patient&lsquo;s video report.</Typography>

          <div style={{ marginTop: '8px' }}>
            <RadioGroup onChange={handleUpdateSelectedPackage} value={selectedPackage}>
              <FormControlLabel
                control={<Radio color="primary" checked={selectedPackage === 'standard'} />}
                label={`Standard Package - Within 5 days - ${pricing.standard}`}
                value="standard"
                style={{ textAlign: 'left' }}
              />
              <FormControlLabel
                control={<Radio color="primary" checked={selectedPackage === 'express'} />}
                label={`Express Package - Within 48 hours - ${pricing.express}`}
                value="express"
                style={{ textAlign: 'left' }}
              />
            </RadioGroup>
          </div>
        </Grid>
      )}
    </Grid>
  );
};
